export type TransferChoice = "IMMEDIATE" | "SCHEDULED";
export interface TransferRequestBody {
  readonly scheduleType: TransferChoice;
  readonly requestedTransferDate?: string;
}
export interface TransferDraft {
  readonly scheduleType: TransferChoice | null;
  readonly date: string;
  readonly request?: { readonly key: string; readonly body: TransferRequestBody };
}

const prefix = "carepass.transaction-transfer.v1:";
const memory = new Map<string, TransferDraft>();
const emptyDraft: TransferDraft = { scheduleType: null, date: "" };
const storageKey = (memberId: number, transactionId: number) => `${prefix}${memberId}:${transactionId}`;

function validBody(body: unknown): body is TransferRequestBody {
  if (!body || typeof body !== "object") return false;
  const value = body as TransferRequestBody;
  return value.scheduleType === "IMMEDIATE" ? value.requestedTransferDate === undefined
    : value.scheduleType === "SCHEDULED" && typeof value.requestedTransferDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.requestedTransferDate);
}

export function readTransferDraft(memberId: number, transactionId: number): TransferDraft {
  const key = storageKey(memberId, transactionId);
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const value = JSON.parse(raw) as TransferDraft;
      if (value && [null, "IMMEDIATE", "SCHEDULED"].includes(value.scheduleType) && typeof value.date === "string"
        && (!value.request || (typeof value.request.key === "string" && value.request.key.length > 0 && validBody(value.request.body)))) {
        memory.set(key, value);
        return value;
      }
      sessionStorage.removeItem(key);
    }
  } catch { /* A blocked storage still supports this tab's in-memory request. */ }
  return memory.get(key) ?? emptyDraft;
}

export function saveTransferDraft(memberId: number, transactionId: number, draft: TransferDraft): TransferDraft {
  const key = storageKey(memberId, transactionId);
  memory.set(key, draft);
  try { sessionStorage.setItem(key, JSON.stringify(draft)); } catch { /* Reuse the same in-memory request key. */ }
  return draft;
}

export function beginTransferRequest(memberId: number, transactionId: number, body: TransferRequestBody): TransferDraft {
  const draft = readTransferDraft(memberId, transactionId);
  if (draft.request) {
    if (draft.request.body.scheduleType !== body.scheduleType || draft.request.body.requestedTransferDate !== body.requestedTransferDate) {
      throw new Error("앞선 송금 요청의 결과를 먼저 확인해 주세요.");
    }
    return draft;
  }
  return saveTransferDraft(memberId, transactionId, {
    ...draft, scheduleType: body.scheduleType, date: body.requestedTransferDate ?? "",
    request: { key: crypto.randomUUID(), body },
  });
}

export function clearTransferDraft(memberId: number, transactionId: number) {
  const key = storageKey(memberId, transactionId);
  memory.delete(key);
  try { sessionStorage.removeItem(key); } catch { /* Memory was already cleared. */ }
}

export function clearTransferDrafts() {
  memory.clear();
  try {
    const keys = Array.from({ length: sessionStorage.length }, (_, index) => sessionStorage.key(index))
      .filter((key): key is string => Boolean(key?.startsWith(prefix)));
    keys.forEach(key => sessionStorage.removeItem(key));
  } catch { /* No readable persistent drafts remain. */ }
}

/** A calendar date in Seoul, independent of the device's local timezone. */
export function minimumTransferDate(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hourCycle: "h23",
  }).formatToParts(now);
  const value = (type: string) => parts.find(part => part.type === type)!.value;
  const day = `${value("year")}-${value("month")}-${value("day")}`;
  if (Number(value("hour")) < 10) return day;
  // Date arithmetic only: noon UTC cannot move this calendar day across a DST boundary.
  const next = new Date(`${day}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

export function transferDateError(value: string, now = new Date()): string | undefined {
  if (!value) return "송금일을 선택해 주세요.";
  const calendar = new Date(`${value}T12:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(calendar.getTime()) || calendar.toISOString().slice(0, 10) !== value) return "송금일을 다시 선택해 주세요.";
  if (value < minimumTransferDate(now)) return "서울 기준 오전 10시가 지나지 않은 날짜를 선택해 주세요.";
  return undefined;
}
